import { Tab, TabGroup, TabList, TabPanel, TabPanels } from "@headlessui/react";
import clsx from "clsx";
import { useEffect, useRef, useState } from "react";

import { Icon } from "../Icon";
import { Icons } from "../Icon/utils";

export default function Tabs({
  tabs,
  className,
  tabListClassName,
  tabClassName,
  tabPanelsClassName,
  tabPanelClassName,
  unmountTabs = true,
  selectedIndex,
  onChange,
}: {
  className?: string;
  onChange?: (index: number) => void;
  selectedIndex?: number;
  tabClassName?: string;
  tabListClassName?: string;
  tabPanelClassName?: string;
  tabPanelsClassName?: string;
  tabs: {
    content: React.ReactNode;
    icon?: keyof typeof Icons;
    id?: string;
    title: React.ReactNode;
  }[];
  unmountTabs?: boolean;
}) {
  const tabListRef = useRef<HTMLDivElement>(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  const checkOverflow = () => {
    const el = tabListRef.current;
    if (el) {
      const hasOverflow = el.scrollWidth > el.clientWidth;
      setShowLeftArrow(hasOverflow && el.scrollLeft > 0);
      setShowRightArrow(
        hasOverflow && el.scrollLeft < el.scrollWidth - el.clientWidth - 1,
      );
    }
  };

  useEffect(() => {
    checkOverflow();
    window.addEventListener("resize", checkOverflow);
    return () => window.removeEventListener("resize", checkOverflow);
  }, [tabs]);

  const scrollLeft = () => {
    if (tabListRef.current) {
      tabListRef.current.scrollBy({ behavior: "smooth", left: -150 });
    }
  };

  const scrollRight = () => {
    if (tabListRef.current) {
      tabListRef.current.scrollBy({ behavior: "smooth", left: 150 });
    }
  };

  return (
    <TabGroup
      className={clsx("w-full", className)}
      selectedIndex={selectedIndex}
      onChange={onChange}
    >
      <div className="relative flex items-center">
        <TabList
          ref={tabListRef}
          onScroll={checkOverflow}
          className={clsx(
            "relative flex border-b w-full border-gray-200 overflow-x-auto scrollbar-hide",
            tabListClassName,
          )}
          style={{ msOverflowStyle: "none", scrollbarWidth: "none" }}
        >
          {tabs.map((tab, index) => (
            <Tab
              key={tab.id ?? index}
              className={clsx("focus:outline-none", tabClassName)}
            >
              {({ selected }) => (
                <div
                  className={clsx(
                    "text-sm w-full justify-center items-center whitespace-nowrap inline-flex cursor-pointer border-b-2 px-2 lg:px-4 py-2 font-semibold transition ease-in hover:text-gray-500 hover:border-gray-500",
                    selected && "border-gray-700 text-gray-900",
                    !selected && "border-transparent text-gray-400",
                  )}
                >
                  <span className="flex items-center gap-2">
                    {tab.icon && (
                      <Icon
                        name={tab.icon}
                        className={clsx(
                          "h-3 w-3",
                          selected ? "text-gray-900" : "text-gray-400",
                        )}
                      />
                    )}
                    <span>{tab.title}</span>
                  </span>
                </div>
              )}
            </Tab>
          ))}
        </TabList>
        {(showLeftArrow || showRightArrow) && (
          <div className="flex items-center h-auto border divide-x ml-2 flex-shrink-0">
            <button
              type="button"
              onClick={scrollLeft}
              disabled={!showLeftArrow}
              className={clsx(
                "px-2 md:[px-1] py-[2px] hover:bg-gray-100 transition-colors",
                showLeftArrow
                  ? "text-gray-600 cursor-pointer"
                  : "text-gray-300 cursor-not-allowed",
              )}
              aria-label="Scroll tabs left"
            >
              <Icon name="fasChevronLeft" className="h-3 w-3" />
            </button>
            <button
              type="button"
              onClick={scrollRight}
              disabled={!showRightArrow}
              className={clsx(
                "px-2 md:[px-1] py-[2px] hover:bg-gray-100 transition-colors",
                showRightArrow
                  ? "text-gray-600 cursor-pointer"
                  : "text-gray-300 cursor-not-allowed",
              )}
              aria-label="Scroll tabs right"
            >
              <Icon name="fasChevronRight" className="h-3 w-3" />
            </button>
          </div>
        )}
      </div>

      <TabPanels
        className={clsx("mt-2 w-full", tabPanelsClassName ?? "h-auto")}
      >
        {tabs.map((tab, index) => (
          <TabPanel
            unmount={unmountTabs}
            key={tab.id ?? index}
            className={clsx("w-full", tabPanelClassName ?? "h-full")}
          >
            {tab.content}
          </TabPanel>
        ))}
      </TabPanels>
    </TabGroup>
  );
}
